import Button from '@ds/components/actions/Button';
import { Flex } from '@ds/components/layout/Flex';
import Typography from '@ds/components/typography/Typography';
import React, { type ErrorInfo, type ReactNode } from 'react';

import styles from './ErrorBoundary.module.css';

/** Render-time fallback. Receives the caught error and a `reset` to clear the boundary. */
export type ErrorBoundaryFallback = (error: Error, reset: () => void) => ReactNode;

export interface ErrorBoundaryProps {
    children: ReactNode;
    /**
     * Custom fallback UI. When omitted, a token-styled default panel is shown.
     * `reset` clears the error so the children re-render (use after the user retries).
     */
    fallback?: ErrorBoundaryFallback;
    /** Side-effect hook for logging/telemetry. Runs in `componentDidCatch`. */
    onError?: (error: Error, info: ErrorInfo) => void;
    /**
     * When any value in this array changes while an error is shown, the boundary
     * resets automatically. Pass `[location.pathname]` so navigating away clears
     * a stuck route error.
     */
    resetKeys?: unknown[];
}

interface ErrorBoundaryState {
    error: Error | null;
}

/** Failed dynamic `import()` of a lazy route chunk — usually a stale build after deploy. */
const isChunkLoadError = (error: Error): boolean =>
    error.name === 'ChunkLoadError' || /Loading chunk [\d]+ failed/i.test(error.message);

/**
 * Catches render-time errors in its subtree and shows a fallback instead of
 * unmounting the whole React tree (which React 18 does by default for an
 * uncaught error). Error boundaries must be class components — there is no hook
 * equivalent.
 *
 * Place one at the app root (catastrophic failures) and one per route
 * (`resetKeys={[location.pathname]}`) so a single page or Relay query failure
 * doesn't blank the entire app.
 *
 * @example
 * <ErrorBoundary resetKeys={[location.pathname]}>
 *     <Switch>{routes}</Switch>
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    componentDidUpdate(prevProps: ErrorBoundaryProps): void {
        const { resetKeys } = this.props;
        if (!this.state.error || !resetKeys) {
            return;
        }
        const changed =
            resetKeys.length !== prevProps.resetKeys?.length ||
            resetKeys.some((key, i) => !Object.is(key, prevProps.resetKeys?.[i]));
        if (changed) {
            this.reset();
        }
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        // eslint-disable-next-line no-console
        console.error('ErrorBoundary caught an error:', error, info.componentStack);
        this.props.onError?.(error, info);
    }

    private reset = (): void => {
        this.setState({ error: null });
    };

    private renderDefaultFallback(error: Error): ReactNode {
        const chunkError = isChunkLoadError(error);
        return (
            <div
                className={styles.fallback}
                role="alert"
                data-chunk-error={chunkError || undefined}
            >
                {/* Heading-2 to body: 12px (--stack-heading-2-to-body) — Figma 14647:3354. The panel gap
                    (16px) and the margin on the actions still put 24px between the body and the buttons. */}
                <Flex
                    direction="column"
                    align="center"
                    gap={3}
                >
                    <Typography
                        variant="heading-2"
                        component="h1"
                    >
                        Something went wrong
                    </Typography>
                    <Typography
                        variant="body-1"
                        color="secondary-1"
                    >
                        {chunkError
                            ? 'A newer version of the app is available. Reload to continue.'
                            : 'An unexpected error occurred. You can try again, or reload the page.'}
                    </Typography>
                </Flex>
                <div className={styles.actions}>
                    {chunkError ? (
                        <Button onClick={() => window.location.reload()}>Reload page</Button>
                    ) : (
                        <>
                            <Button onClick={this.reset}>Try again</Button>
                            <Button
                                variant="secondary"
                                onClick={() => window.location.reload()}
                            >
                                Reload page
                            </Button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    render(): ReactNode {
        const { error } = this.state;
        if (error) {
            return this.props.fallback?.(error, this.reset) ?? this.renderDefaultFallback(error);
        }
        return this.props.children;
    }
}

export { ErrorBoundary };
export default ErrorBoundary;
